import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { Modal } from 'react-bootstrap';
import { componentClasses as baseComponentClasses } from '../livestreams/utils/livestreamDesignSystem';
import { formatTime } from '../../utils/helper';
import { getThumbnailUrl, formatFileSize } from '../../utils/recordingApi';
import { useRecordingPermissions } from '../../hooks/useRecordingPermissions';
import PurchaseModal from './PurchaseModal';
import RecordingTrimCrop from './RecordingTrimCrop';
import DownloadManager from './DownloadManager';

// Modern Tailwind classes with brand colors for recording components
const componentClasses = {
  ...baseComponentClasses,
  // Enhanced recording card styles with brand colors
  card: "bg-white rounded-2xl shadow-lg border border-gray-100 hover:shadow-2xl hover:border-[#00a693] transition-all duration-300 hover:-translate-y-2 group",
  cardContent: "p-4",
  cardHeader: "mb-4",
  cardTitle: "text-xl font-bold text-[#010101] mb-2 group-hover:text-[#00a693] transition-colors duration-300",
  cardSubtitle: "text-sm text-[#666666] mb-3",
  cardBody: "mb-3 space-y-2",
  cardActions: "flex flex-col gap-3",
  infoItem: "text-sm text-[#666666] flex items-center gap-2",
  price: "text-2xl font-bold text-[#00a693]",
  thumbnail: "w-full h-48 object-cover rounded-xl mb-4 group-hover:rounded-2xl transition-all duration-300",
  overlay: "absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent flex items-end justify-center rounded-xl p-4",
  overlayContent: "text-white text-center w-full",
  overlayTitle: "text-lg font-semibold mb-2 text-white",
  overlaySubtitle: "text-sm opacity-90 text-white",
  button: {
    // Watch button with brand teal color - compact for cards
    primary: "px-4 py-2 bg-[#00a693] text-white rounded-lg hover:bg-[#008a7a] hover:shadow-md hover:shadow-[#00a693]/20 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 font-medium text-xs tracking-wide flex items-center gap-1.5",
    // Purchase button with brand orange - compact for cards
    purchase: "px-4 py-2 bg-[#ff6f3c] text-white rounded-lg hover:bg-[#e55a2b] hover:shadow-md hover:shadow-[#ff6f3c]/20 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 font-medium text-xs tracking-wide flex items-center gap-1.5",
    // Secondary button with brand colors
    secondary: "px-4 py-2 bg-gray-100 text-[#010101] rounded-lg hover:bg-gray-200 hover:shadow-sm transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 font-medium text-xs",
    // Icon buttons with brand colors - smaller for cards
    iconOnly: "w-8 h-8 p-0 rounded-lg bg-gray-50 hover:bg-[#00a693] hover:text-white flex items-center justify-center transition-all duration-200 hover:shadow-md hover:shadow-[#00a693]/20 hover:-translate-y-0.5 active:translate-y-0 text-[#666666]",
    // Danger button with brand red
    danger: "px-4 py-2 bg-[#BD2026] text-white rounded-lg hover:bg-[#a01c22] hover:shadow-md hover:shadow-[#BD2026]/20 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 font-medium text-xs tracking-wide",
    // Success button with brand teal
    success: "px-4 py-2 bg-[#00a693] text-white rounded-lg hover:bg-[#008a7a] hover:shadow-md hover:shadow-[#00a693]/20 transition-all duration-200 hover:-translate-y-0.5 active:translate-y-0 font-medium text-xs tracking-wide"
  }
};

// Recording Card Component
function RecordingCard({ 
  recording, 
  user, 
  onPurchase, 
  onWatch, 
  onDownload, 
  onRename, 
  onDelete, 
  onTrimCrop
}) {
  console.log('🔍 RecordingCard - Component Rendered for Recording ID:', recording?.id);
  
  // Use the new permissions hook
  const permissions = useRecordingPermissions(recording);
  
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [newTitle, setNewTitle] = useState(recording.title || '');
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showTrimCropModal, setShowTrimCropModal] = useState(false);
  const [showDownloadManager, setShowDownloadManager] = useState(false);

  // Debug logging for recording object
  console.log('🔍 RecordingCard - Received Recording Object:', {
    id: recording.id,
    title: recording.title,
    description: recording.description,
    duration: recording.duration,
    size: recording.size,
    courtName: recording.courtName,
    recordingDate: recording.recordingDate,
    livestreamTitle: recording.livestreamTitle,
    cameraName: recording.cameraName,
    purchased: recording.purchased,
    price: recording.price,
    thumbnails: recording.thumbnails,
    presignedUrl: recording.presignedUrl
  });

  // Get thumbnail URL
  const thumbnailUrl = getThumbnailUrl(recording);
  console.log('🔍 RecordingCard - Final Thumbnail URL:', thumbnailUrl);

  const handleRename = async () => {
    if (!newTitle.trim()) {
      toast.error('Title cannot be empty');
      return;
    }

    const success = await onRename(recording.id, newTitle.trim());
    if (success) {
      setShowRenameModal(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this recording? This action cannot be undone.')) {
      return;
    }

    const success = await onDelete(recording.id);
    if (success) {
      // Refresh the recordings list
      window.location.reload();
    }
  };

  const handleTrimCrop = () => {
    setShowTrimCropModal(true);
  };

  const handlePurchaseSuccess = () => {
    // Refresh the recordings list or update the recording status
    window.location.reload();
  };


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className={componentClasses.card}>
      {/* Thumbnail */}
      <div className="relative aspect-video bg-gray-200 rounded-t-lg overflow-hidden">
        <img
          src={thumbnailUrl}
          alt={recording.title || 'Recording thumbnail'}
          className="w-full h-full object-cover"
          crossOrigin="anonymous"
          onError={(e) => {
            console.log('🔍 RecordingCard - Thumbnail Error:', e);
            e.target.src = '/fallback-thumbnail.png';
          }}
          onLoad={(e) => {
            console.log('🔍 RecordingCard - Thumbnail Loaded Successfully:', e.target.src);
          }}
          onLoadStart={(e) => {
            console.log('🔍 RecordingCard - Thumbnail Loading Started:', e.target.src);
          }}
        />
        
        {/* Access overlay for non-entitled users (but not admins/owners) */}
        {!permissions.isEntitled && !permissions.isAdmin && !permissions.isOwner && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="text-white text-center">
              <div className="text-2xl mb-2">🔒</div>
              <div className="text-sm">Purchase Required</div>
            </div>
          </div>
        )}
      </div>

      {/* Card Content */}
      <div className={componentClasses.cardContent}>
        {/* Header */}
        <div className={componentClasses.cardHeader}>
          <h3 className={componentClasses.cardTitle}>
            {recording.title || recording.livestreamTitle || 'Untitled Recording'}
          </h3>
          <div className={componentClasses.cardSubtitle}>
            {recording.courtName && (
              <span className="flex items-center gap-1">
                🏟️ {recording.courtName}
              </span>
            )}
          </div>
        </div>

        {/* Body */}
        <div className={componentClasses.cardBody}>
          <div className="space-y-2">
            {recording.recordingDate && (
              <div className={componentClasses.infoItem}>
                <span className="flex items-center gap-1">
                  📅 {formatDate(recording.recordingDate)}
                </span>
              </div>
            )}
            
            {recording.cameraName && (
              <div className={componentClasses.infoItem}>
                <span className="flex items-center gap-1">
                  📹 {recording.cameraName}
                </span>
              </div>
            )}
            
            {recording.duration && (
              <div className={componentClasses.infoItem}>
                <span className="flex items-center gap-1">
                  ⏱️ {formatDuration(recording.duration)}
                </span>
              </div>
            )}
            
            {recording.size && (
              <div className={componentClasses.infoItem}>
                <span className="flex items-center gap-1">
                  💾 {formatFileSize(recording.size)}
                </span>
              </div>
            )}
            
            {recording.price !== undefined && (
              <div className={componentClasses.infoItem}>
                <span className={componentClasses.price}>
                  {recording.price === 0 || recording.price === null ? 'Free' : `$${recording.price}`}
                </span>
              </div>
            )}
            
            {recording.expirationDate && (
              <div className={componentClasses.infoItem}>
                <span className="flex items-center gap-1 text-orange-600">
                  ⏰ Expires: {formatDate(recording.expirationDate)}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className={componentClasses.cardActions}>
          {/* Primary Action */}
          {permissions.shouldShowWatchButton ? (
            <button
              onClick={() => onWatch(recording)}
              className={componentClasses.button.primary}
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z" clipRule="evenodd" />
              </svg>
              Watch
            </button>
          ) : permissions.shouldShowPurchaseButton ? (
            <button
              onClick={() => setShowPurchaseModal(true)}
              className={componentClasses.button.purchase}
            >
              <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
              </svg>
              Purchase
            </button>
          ) : (
            <button
              disabled
              className="px-4 py-2 bg-gray-100 text-gray-400 rounded-lg cursor-not-allowed font-medium text-xs"
            >
              No Access
            </button>
          )}

          {/* Secondary Actions */}
          <div className="flex gap-1.5 flex-wrap">
            {permissions.canDownload && (
              <button
                onClick={() => setShowDownloadManager(true)}
                className={componentClasses.button.iconOnly}
                title="Download Recording"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            )}
            
            {permissions.canRename && (
              <button
                onClick={() => setShowRenameModal(true)}
                className={componentClasses.button.iconOnly}
                title="Rename"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              </button>
            )}
            
            {permissions.canTrimCrop && (
              <button
                onClick={handleTrimCrop}
                className={componentClasses.button.iconOnly}
                title="Trim/Crop"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </button>
            )}
            
            {permissions.canDelete && (
              <button
                onClick={handleDelete}
                className={componentClasses.button.iconOnly}
                title="Delete"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        </div>
      </div>


      {/* Rename Modal */}
      {showRenameModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Rename Recording</h3>
            <input
              type="text"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              className="w-full p-2 border border-gray-300 rounded mb-4"
              placeholder="Enter new title"
            />
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowRenameModal(false)}
                className={componentClasses.button.secondary}
              >
                Cancel
              </button>
              <button
                onClick={handleRename}
                className={componentClasses.button.primary}
              >
                Rename
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Modal */}
      <PurchaseModal
        recording={recording}
        isOpen={showPurchaseModal}
        onClose={() => setShowPurchaseModal(false)}
        onSuccess={handlePurchaseSuccess}
      />

      {/* Trim/Crop Modal */}
      <Modal
        show={showTrimCropModal}
        onHide={() => setShowTrimCropModal(false)}
        centered
        backdrop="static"
        keyboard={false}
        size="xl"
        aria-label="Trim and Crop Recording Modal"
      >
        <Modal.Header closeButton>
          <Modal.Title>Trim & Crop Recording</Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ padding: 0 }}>
          <RecordingTrimCrop
            recording={recording}
            onClose={() => setShowTrimCropModal(false)}
          />
        </Modal.Body>
      </Modal>

      {/* Download Manager Modal */}
      {showDownloadManager && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-md w-full mx-4 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Download Recording
              </h3>
              <button
                onClick={() => setShowDownloadManager(false)}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            <div className="mb-4">
              <h4 className="font-medium text-gray-900 mb-2">
                {recording.title || recording.livestreamTitle || 'Untitled Recording'}
              </h4>
              <div className="text-sm text-gray-600 space-y-1">
                {recording.duration && (
                  <div>Duration: {formatDuration(recording.duration)}</div>
                )}
                {recording.size && (
                  <div>Size: {formatFileSize(recording.size)}</div>
                )}
              </div>
            </div>

            <DownloadManager
              recordingId={recording.id}
              filename={recording.title || `recording-${recording.id}.mp4`}
              onDownloadStart={() => console.log('Download started')}
              onDownloadComplete={() => {
                setShowDownloadManager(false);
                toast.success('Download completed successfully!');
              }}
              onDownloadError={(error) => {
                console.error('Download error:', error);
                toast.error('Download failed. Please try again.');
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default RecordingCard;
